import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from 'react';

import { type StateCode } from '@cx-modules/types';

import { type APIResponse } from '~/types/api-response';

export type LoaderProviderProps = {
	DISABLE_ODDS_FORMAT_SELECTION?: boolean;
	apiResponse?: APIResponse;
	baseUrl: string;
	currentPath: string;
	currentPathWithRepub?: string;
	deviceType: 'DESKTOP' | 'TABLET' | 'MOBILE';
	state: StateCode | '';
};

export type LoaderProviderData = LoaderProviderProps;

export const LoaderContext = createContext<LoaderProviderData>(
	undefined as any as LoaderProviderData,
);
LoaderContext.displayName = 'LoaderContext';

export const LoaderProvider = ({
	DISABLE_ODDS_FORMAT_SELECTION,
	apiResponse,
	baseUrl,
	children,
	currentPath,
	currentPathWithRepub,
	deviceType,
	state,
	...props
}: PropsWithChildren<LoaderProviderData>) => {
	const value = useMemo(
		() => ({
			DISABLE_ODDS_FORMAT_SELECTION,
			apiResponse,
			baseUrl,
			currentPath,
			currentPathWithRepub,
			deviceType,
			state,
		}),
		[
			DISABLE_ODDS_FORMAT_SELECTION,
			apiResponse,
			baseUrl,
			currentPath,
			currentPathWithRepub,
			deviceType,
			state,
		],
	);

	return (
		<LoaderContext.Provider value={value} {...props}>
			{children}
		</LoaderContext.Provider>
	);
};

export const useLoaderContext = () => {
	const context = useContext(LoaderContext);
	if (!context) {
		throw new Error(
			'useLoaderContext can only be used inside of LoaderProvider',
		);
	}

	return context;
};
